// PR-J — break-glass emergency TPS switch for a single loan. Voids the
// outgoing TPS's unpaid bills + pending distributions and flips the loan to a
// new active, certified servicer immediately. Blocked server-side when the
// outgoing TPS still holds cleared-but-undistributed funds (or the target is
// inactive / not certified) — that message is surfaced via a toast. Gated
// Portfolio.EmergencySwitchTps (the caller renders this only for holders).

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { Button, FormField } from "@/design-system/components/ui";
import { Select, Textarea } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { useEmergencyTpsSwitch } from "@/hooks/portfolioAdmin/useTpsOversight";
import { useTpsList, useTpsReadiness } from "@/hooks/portfolioAdmin/useTps";
import { useEntityNameMap } from "@/hooks/capitalMarkets/useCounterpartyNames";
import {
  buildReadinessMap,
  tpsOptionDisabled,
  tpsOptionLabel,
} from "@/lib/tpsPicker";
import type { TpsDto } from "@/types/tps";

export function EmergencyTpsSwitchModal({
  loanId,
  loanNumber,
  currentTpsId,
  open,
  onClose,
}: {
  loanId: string;
  loanNumber: string;
  currentTpsId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const emergencySwitch = useEmergencyTpsSwitch(loanId);
  const listQuery = useTpsList({ status: "Active", pageSize: 200 }, open);
  const { map: entityNames } = useEntityNameMap(open);

  // Targets exclude the loan's current TPS. Certification is enforced
  // server-side (a non-certified target is rejected with a clear message).
  const options = useMemo<TpsDto[]>(
    () =>
      (listQuery.data?.data ?? []).filter((t) => t.id !== currentTpsId),
    [listQuery.data, currentTpsId],
  );

  const readinessQuery = useTpsReadiness(open);
  const readiness = useMemo(
    () => buildReadinessMap(readinessQuery.data),
    [readinessQuery.data],
  );

  const [newTpsId, setNewTpsId] = useState("");
  const [reason, setReason] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);

  const reasonInvalid = reason.trim() === "";
  const targetInvalid = newTpsId === "";
  const selected = options.find((t) => t.id === newTpsId);

  const submit = () => {
    if (reasonInvalid) {
      toast.error("A reason is required.");
      return;
    }
    if (targetInvalid) {
      toast.error("Select a target TPS first.");
      return;
    }
    setSubmitError(null);
    emergencySwitch.mutate(
      { newTpsId, reason: reason.trim() },
      {
        onSuccess: (result) => {
          toast.success(
            `Switched. Voided ${result.voidedBills} bill(s) + ${result.voidedDistributions} distribution(s).`,
          );
          setNewTpsId("");
          setReason("");
          onClose();
        },
        onError: (err) => setSubmitError(extractErrorMessage(err)),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Emergency TPS switch — loan ${loanNumber}`}
      description="Break-glass reassignment for a servicer in distress."
      size="md"
      footer={
        <div className="flex items-center justify-end gap-space-2">
          <Button variant="text" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            size="sm"
            loading={emergencySwitch.isPending}
            disabled={reasonInvalid || targetInvalid}
            onClick={submit}
            data-testid="tps-emergency-switch-submit"
          >
            Switch immediately
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-space-4">
        <div
          className="flex items-start gap-space-3 rounded-md border border-status-error/40 bg-status-error/10 p-space-3"
          role="alert"
        >
          <AlertTriangle
            size={18}
            aria-hidden
            className="mt-[2px] shrink-0 text-status-error"
          />
          <p className="m-0 text-small text-onyx">
            This <span className="font-semibold">immediately</span> flips the
            loan to the new servicer and{" "}
            <span className="font-semibold">voids</span> the outgoing TPS's
            unpaid bills and pending distributions. It is blocked if the
            outgoing TPS still holds cleared funds that haven't been
            distributed.
          </p>
        </div>

        <FormField label="New TPS provider" required>
          <Select
            value={newTpsId}
            onChange={(e) => {
              setNewTpsId(e.target.value);
              setSubmitError(null);
            }}
            aria-label="New TPS provider"
            data-testid="tps-emergency-target"
          >
            <option value="">Select a TPS…</option>
            {options.map((t) => (
              <option
                key={t.id}
                value={t.id}
                disabled={tpsOptionDisabled(t, readiness)}
              >
                {tpsOptionLabel(t, entityNames.get(t.entityId), readiness)}
              </option>
            ))}
          </Select>
        </FormField>

        {submitError && (
          <p
            className="text-small text-status-error m-0"
            role="alert"
            data-testid="tps-emergency-error"
          >
            {submitError}
            {selected && !selected.isDefault && (
              <>
                {" "}
                <Link
                  to={`/portfolio-admin/tps/${selected.id}`}
                  className="text-gold-shadow underline"
                >
                  Open TPS setup
                </Link>
              </>
            )}
          </p>
        )}

        <FormField label="Reason" required>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this loan being switched under emergency conditions?"
            invalid={reason !== "" && reasonInvalid}
            aria-label="Reason"
            data-testid="tps-emergency-reason"
          />
        </FormField>
      </div>
    </Modal>
  );
}
